// @flow
import React from 'react';

import ChatMessage from './ChatMessage';
import ChatBoxInput from './ChatBoxInput';
import GroupChatMember from './GroupChatMember';
import GroupChatFileStorage from './GroupChatFileStorage';
import GroupChatClient from '../../models/group_chats/GroupChatClient';
import type { Message } from '../../models/group_chats/Message';
import type { Company } from '../../models/group_chats/Company';
import type { Supporter } from '../../models/supporters/Supporter';
import type { Staff } from '../../models/staffs/Staff';

type Props = {
  viewerId: number,
  viewerType: string,
  groupChatId: number,
  messages: Array<any>,
  groupChatName: string,
  groupChatIconURL: string,
  companyName?: string,
  companyProfileImageURL: string,
  company: Company,
  supporters: Array<Supporter>,
  staffs: Array<Staff>,
  isMobile: boolean,
};

type State = {
  messages: Array<any>,
  isLoadMore: boolean,
  chatBoxHeight: number,
};

export default class GroupChatView extends React.Component<Props, State> {
  groupChatClient: GroupChatClient;
  conversationNode: any;
  chatBoxInputNode: any;
  nextMessagePage: number = 2;

  constructor(props: Props) {
    super(props);
    this.state = {
      messages: this.props.messages || [],
      isLoadMore: false,
      chatBoxHeight: this.getChatBoxHeight(),
      inputFocused: false,
    };
    this.groupChatClient = new GroupChatClient(this.props.groupChatId);
    this.groupChatClient.received = this.handleReceivedMessage;
  }

  componentDidMount() {
    this.scrollToBottom();
  }

  render() {
    const {
      groupChatName,
      viewerId,
      viewerType,
      groupChatIconURL,
      companyName,
      supporters,
      company,
      companyProfileImageURL,
      groupChatId,
      isMobile,
      staffs,
    } = this.props;

    const { messages, isLoadMore, chatBoxHeight } = this.state;

    const members = this.props.supporters
      .concat([this.props.company])
      .concat(staffs)
      .filter(member => member.id !== viewerId || member.type !== viewerType);

    return (
      <div
        className="card border-light chat-box"
        style={{ height: chatBoxHeight }}
      >
        <div className="card-header">
          <img src={groupChatIconURL} width={40} className="img-circle mr-5" />
          <strong className="ml-5">{groupChatName}</strong>
          <div className="card-body pb-10 pt-10">
            <div className="float-left c-gray">
              <strong>{companyName}</strong>
            </div>
            <GroupChatMember
              groupChatSize={supporters.length + 1 + staffs.length}
              company={company}
              supporters={supporters}
              companyImage={companyProfileImageURL}
              staffs={staffs}
            />
            <GroupChatFileStorage groupChatId={groupChatId} />
          </div>
        </div>
        <div className="chat-box__conversation-wrapper">
          <div
            onScroll={this.handleChatBoxScroll}
            className="chat-box__conversation pt-30"
            ref={this.setConversationNode}
          >
            {isLoadMore && (
              <div>
                <div className="pulse-loading" />
                <div className="chat-box__loadmore">さらに読み込む</div>
              </div>
            )}
            {messages
              .concat(this.groupChatClient.getBufferedMessages())
              .map(message => (
                <ChatMessage
                  id={message.id}
                  key={message.id}
                  timestamp={message.createdAt}
                  profilePictureURL={message.senderProfileImage}
                  message={message.body}
                  attachmentName={message.attachmentName}
                  attachmentURL={message.attachmentUrl}
                  other={
                    message.senderId &&
                    (viewerId !== message.senderId ||
                      viewerType !== message.senderType)
                  }
                  loading={message.loading}
                  senderName={message.senderName}
                  numberOfSeenMember={message.numberOfSeenMember}
                  groupChatSize={supporters.length + 1}
                  groupChatId={groupChatId}
                  viewerId={viewerId}
                  viewerType={viewerType}
                  onMentionClick={this.handleMentionClick}
                />
              ))}
          </div>
        </div>
        <ChatBoxInput
          ref={e => (this.chatBoxInputNode = e)}
          members={members}
          onKeyUp={this.handleInputKeyUp}
          onSubmit={this.sendMessageHandler}
          isMobile={isMobile}
          chatBoxHeight={chatBoxHeight}
        />
      </div>
    );
  }

  getChatBoxHeight(): number {
    const windowHeight =
      window.innerHeight || // $FlowFixMe
      document.documentElement.clientHeight || // $FlowFixMe
      document.body.clientHeight;
    return Math.max(windowHeight * 0.9, 512);
  }

  scrollToBottom() {
    if (!this.conversationNode) return;
    $(this.conversationNode).animate(
      {
        scrollTop: this.conversationNode.scrollHeight,
      },
      200,
    );
  }

  handleMentionClick = (e: { mentionId: number, type: string }) => {
    const { mentionId, type } = e;
    if (mentionId == this.props.viewerId && type === this.props.viewerType) {
      return;
    }
    this.chatBoxInputNode.handleMentionClick(e);
  };

  handleChatBoxScroll = () => {
    if (this.conversationNode.scrollTop > 0 || this.state.isLoadMore) return;
    this.setState({ isLoadMore: true });
    this.groupChatClient
      .fetchMessages(this.nextMessagePage)
      .then(response => {
        if (response.data.length == 0) return;
        this.setState(oldState => ({
          messages: [...response.data, ...oldState.messages],
        }));
        this.conversationNode.scrollTop = 20 * 100; // 20 msgs x circa 100px
        this.nextMessagePage += 1;
      })
      .catch(error => console.log(error))
      .then(() => this.setState({ isLoadMore: false }));
  };

  setConversationNode = (element: any) => {
    this.conversationNode = element;
  };

  getChatMessage(): Message {
    return {
      name: '',
      body: this.chatBoxInputNode.getValue().trim(),
      attachment: this.chatBoxInputNode.getAttachment(),
      senderId: this.props.viewerId,
      senderType: this.props.viewerType,
      senderName: '',
      groupChatId: this.props.groupChatId,
      groupChatCategoryId: 0,
      groupChatName: this.props.groupChatName,
      companyId: this.props.company.id,
      companyName: this.props.company.name,
    };
  }

  handleReceivedMessage = (message: any) => {
    if (message.metaData) {
      this.setState(oldState => ({
        messages: this._sortByDate([...oldState.messages, message]),
      }));
      this.groupChatClient.markAllAsRead(
        this.props.viewerId,
        this.props.viewerType,
      );
      this.scrollToBottom();
    } else {
      const numberOfSeenMember = message.seen;
      const elements = $('.chat-message__member-seen--number')
        .eq(-2)
        .text()
        .split(' ');
      if (parseInt(elements[elements.length - 1]) < numberOfSeenMember) {
        $('.chat-message__member-seen--number').text(
          ' 既読 ' + numberOfSeenMember,
        );
      } else {
        $('.chat-message__member-seen--number:last').text(
          ' 既読 ' + numberOfSeenMember,
        );
      }
    }
  };

  sendMessageHandler = (e?: MouseEvent) => {
    if (e) e.preventDefault();
    if (
      !this.chatBoxInputNode.getAttachment() &&
      this.chatBoxInputNode.getValue().trim().length == 0
    ) {
      return;
    }
    this.groupChatClient.sendMessage(this.getChatMessage());
    this.chatBoxInputNode.reset();
    this.scrollToBottom();
  };

  handleInputKeyUp = (e: KeyboardEvent) => {
    if (e.key == 'Enter' && e.shiftKey) {
      this.sendMessageHandler();
    }
  };

  _sortByDate(messages: Array<any>) {
    let position = null;
    for (let i = messages.length - 1; i > 1; i--) {
      if (
        !messages[i].metaData ||
        !messages[i - 1].metaData ||
        messages[i].metaData.sentAt > messages[i - 1].metaData.sentAt
      )
        break;
      position = i;
    }
    if (position) messages.splice(position - 1, 0, messages.pop());
    return messages;
  }
}
